import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { Add as AddIcon, Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const VATTaxesPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [vatReturns, setVatReturns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadVATReturns();
  }, [page, rowsPerPage, searchQuery]);

  const loadVATReturns = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockVATReturns = [
        { id: 1, period: 'Q2 2026', client: 'Atlas Construction Co', dueDate: '2026-07-19', vatCollected: 52500, vatPaid: 18200, netVAT: 34300, status: 'Pending' },
        { id: 2, period: 'Q1 2026', client: 'Meridian Logistics', dueDate: '2026-04-19', vatCollected: 38700, vatPaid: 15300, netVAT: 23400, status: 'Submitted' },
        { id: 3, period: 'Q2 2026', client: 'Orchard Foods Ltd', dueDate: '2026-07-19', vatCollected: 41600, vatPaid: 19800, netVAT: 21800, status: 'Pending' },
        { id: 4, period: 'Q1 2026', client: 'Seabreeze Properties', dueDate: '2026-04-19', vatCollected: 31900, vatPaid: 12100, netVAT: 19800, status: 'Submitted' },
        { id: 5, period: 'Q1 2026', client: 'Helix Software BV', dueDate: '2026-04-19', vatCollected: 27000, vatPaid: 8500, netVAT: 18500, status: 'Paid' },
      ];
      setVatReturns(mockVATReturns);
      setTotalCount(mockVATReturns.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'period', label: t('period'), minWidth: 100 },
    { id: 'client', label: t('client'), minWidth: 180 },
    { id: 'dueDate', label: t('due_date'), minWidth: 110 },
    { id: 'vatCollected', label: t('vat_collected'), minWidth: 130, align: 'right' },
    { id: 'vatPaid', label: t('vat_paid'), minWidth: 120, align: 'right' },
    { id: 'netVAT', label: t('net_vat'), minWidth: 120, align: 'right' },
    { id: 'status', label: t('status'), minWidth: 100 },
    { id: 'actions', label: t('actions'), minWidth: 80, align: 'center' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Submitted':
        return { bg: '#E3F2FD', color: '#2196F3' };
      case 'Pending':
        return { bg: '#FFF3E0', color: '#FF9800' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const renderRow = (vatReturn) => {
    const statusColor = getStatusColor(vatReturn.status);
    return (
      <TableRow hover key={vatReturn.id}>
        <TableCell sx={{ fontWeight: 500 }}>{vatReturn.period}</TableCell>
        <TableCell>{vatReturn.client}</TableCell>
        <TableCell>{vatReturn.dueDate}</TableCell>
        <TableCell align="right">€{vatReturn.vatCollected.toLocaleString()}</TableCell>
        <TableCell align="right">€{vatReturn.vatPaid.toLocaleString()}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 500, color: '#4CAF50' }}>
          €{vatReturn.netVAT.toLocaleString()}
        </TableCell>
        <TableCell>
          <Chip
            label={vatReturn.status}
            size="small"
            sx={{
              backgroundColor: statusColor.bg,
              color: statusColor.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_vat_taxes')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('manage_vat_returns')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_vat_return')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_vat_returns_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={vatReturns}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_vat_returns_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default VATTaxesPage;
